import periodsLibrary, {
  AppFilterType,
  AppTypedFilters,
  FilterTypeValue,
} from "@/components/filter/filter.types.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { useOrganizationViewFilter } from "@/queries/settings/use-organization.query.ts";
import { shouldAddLifecycleStatusFilterCount } from "@/types/employees/employee.types.ts";
import { EmployeeFieldPermission } from "@/types/role.types.ts";
import { ApiFilterAbstract } from "@skailer-com/uikit/core";
import {
  FilterButton,
  FilterConfig,
  UseFilterActiveFilter as UIKitUseFilterActiveFilter,
  useFilterCore,
  UseFilterResult,
  useModals,
} from "@skailer-com/uikit";
import { ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { AppFilterModal } from "./AppFilterModal.tsx";

export interface FilterButtonRenderProps {
  onClick: () => void;
  onClear: () => void;
  count: number;
}

export interface UseAppFilterConfig extends FilterConfig<FilterTypeValue> {
  renderFilterButton?: (props: FilterButtonRenderProps) => ReactNode;
}

export interface FilterButtonOptions {
  clearable?: boolean;
}

export interface UseAppFilterResult<T extends ApiFilterAbstract> extends UseFilterResult<
  T,
  FilterTypeValue
> {
  filterButton: (options?: FilterButtonOptions) => ReactNode;
  clearFilters: () => void;
  openFilter: () => void;
  isFilterReady: boolean;
}

export type UseFilterActiveFilterTypedFilters = AppTypedFilters;
export type UseFilterActiveFilter<T extends ApiFilterAbstract> = UIKitUseFilterActiveFilter<T>;

export const useAppFilter = <T extends ApiFilterAbstract>(
  config: UseAppFilterConfig
): UseAppFilterResult<T> => {
  const modalsService = useModals();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { canCurrentEmployeeViewField } = usePermissions();
  const lifecycleStatusFilter = config.filters.find(
    (item) => item.type === AppFilterType.employeeLifecycleStatus
  );
  const lifecycleStatusSearchParam =
    lifecycleStatusFilter?.searchParamName || lifecycleStatusFilter?.name;
  const hasExplicitLifecycleStatus = lifecycleStatusSearchParam
    ? searchParams.has(lifecycleStatusSearchParam)
    : false;
  const canViewLifecycleStatus = canCurrentEmployeeViewField(
    EmployeeFieldPermission.employee_lifecycle_status
  );
  const organizationViewFilterQuery = useOrganizationViewFilter(
    Boolean(lifecycleStatusFilter) && canViewLifecycleStatus && !hasExplicitLifecycleStatus
  );
  const { data: organizationViewFilter } = organizationViewFilterQuery;
  const isFilterReady =
    !lifecycleStatusFilter ||
    !canViewLifecycleStatus ||
    hasExplicitLifecycleStatus ||
    !organizationViewFilterQuery.isPending;
  const effectiveFilters = useMemo(
    () =>
      config.filters.map((item) =>
        item.type === AppFilterType.employeeLifecycleStatus
          ? {
              ...item,
              defaultValue: searchParams.has(item.searchParamName || item.name)
                ? []
                : (organizationViewFilter?.employee_lifecycle_statuses ?? []),
            }
          : item
      ),
    [config.filters, organizationViewFilter?.employee_lifecycle_statuses, searchParams]
  );

  const coreResult = useFilterCore<T, FilterTypeValue>(
    { ...config, filters: effectiveFilters },
    {
      periodLibrary: periodsLibrary,
    }
  );
  const shouldAddLifecycleStatusCount =
    Boolean(lifecycleStatusFilter && !lifecycleStatusFilter.hideInFilter) &&
    canViewLifecycleStatus &&
    shouldAddLifecycleStatusFilterCount(
      searchParams,
      organizationViewFilter?.employee_lifecycle_statuses
    );
  const activeFilter = {
    ...coreResult.filter,
    count: coreResult.filter.count + (shouldAddLifecycleStatusCount ? 1 : 0),
  };

  function handleFilterClick() {
    modalsService.openModal(AppFilterModal, {
      filters: effectiveFilters,
      currentFilter: activeFilter,
      source: config.source,
    });
  }

  function handleClearFilter() {
    const newParams = new URLSearchParams();
    const currentSearchParams = new URLSearchParams(window.location.search);

    const allAvailableFilterSearchParams = effectiveFilters.map(
      (item) => item.searchParamName || item.name
    );
    allAvailableFilterSearchParams.push("page");

    currentSearchParams.forEach((value, key) => {
      if (!allAvailableFilterSearchParams.includes(key)) {
        newParams.set(key, value);
      }
    });
    if (lifecycleStatusFilter) {
      newParams.set(lifecycleStatusFilter.searchParamName || lifecycleStatusFilter.name, "");
    }
    setSearchParams(newParams);
  }

  const filterButton = (options?: FilterButtonOptions) => {
    if (config.renderFilterButton) {
      return config.renderFilterButton({
        onClick: handleFilterClick,
        onClear: handleClearFilter,
        count: activeFilter.count,
      });
    }
    return (
      <FilterButton
        count={activeFilter.count}
        onClick={handleFilterClick}
        onClear={options?.clearable ? handleClearFilter : undefined}
        title={t("common:filter")}
      />
    );
  };

  return {
    ...coreResult,
    filter: activeFilter,
    filterButton,
    clearFilters: handleClearFilter,
    openFilter: handleFilterClick,
    isFilterReady,
  };
};
